import { Chip, SxProps, Theme, Typography } from '@mui/material';
import { Box } from '@mui/system';
import { useTranslation } from 'react-i18next';

import { VendorActivationStatus } from 'entities/Vendors';
import { DynamicTooltip } from 'shared/ui/Tooltip';

import { STATUS_CONFIG } from '../lib/config/supplierStatusConfig';

interface SupplierStatusChipProps {
    status: VendorActivationStatus;
    sx?: SxProps<Theme>;
}

export const SupplierStatusChip = ({ status, sx }: SupplierStatusChipProps) => {
    const { t } = useTranslation();
    const config = STATUS_CONFIG[status as keyof typeof STATUS_CONFIG];

    if (!config) return null;

    const tooltipTitle = t(config.tooltipTitleKey, config.tooltipTitleDefault);

    return (
        <DynamicTooltip
            placement="top"
            content={
                <Box>
                    {tooltipTitle && (
                        <Typography
                            variant="subtitle2"
                            sx={{
                                fontWeight: 600,
                                color: config.color,
                                marginBottom: '6px',
                            }}
                        >
                            {tooltipTitle}
                        </Typography>
                    )}
                    <Typography
                        variant="body2"
                        sx={{ lineHeight: 1.4, whiteSpace: 'pre-line' }}
                    >
                        {t(config.tooltipBodyKey, config.tooltipBodyDefault)}
                    </Typography>
                </Box>
            }
        >
            <Chip
                icon={config.icon}
                label={t(config.labelKey, config.labelDefault)}
                size="small"
                data-testclass="supplier-status-chip"
                sx={{
                    backgroundColor: config.backgroundColor,
                    color: config.color,
                    fontWeight: 700,
                    fontFamily: 'Open Sans',
                    fontSize: '10px',
                    borderRadius: '14px',
                    padding: '4px 4px',
                    cursor: 'pointer',
                    '& .MuiChip-label': { paddingRight: '8px' },
                    ...sx,
                }}
            />
        </DynamicTooltip>
    );
};
